import { assistantErrorEntry, type AssistantErrorEntry } from "@/api/form-builder/assistantError";
import { create, type StateCreator } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { tabStorage } from "./tabStorage";
import {
  requestAssignmentsAssistant,
  requestFormAssistant,
} from "@/api/form-builder/formBuilder.mutations";
import type {
  AssignmentsAssistantRequest,
  AssignmentsAssistantResponse,
  AssignmentsFocus,
  AssistantChatMessage,
  AssistantFormChangeRequest,
  AssistantNewFormRequest,
  AssistantPlan,
  AssistantRequest,
  AssistantResponse,
  ProposedAssignmentChange,
} from "@/api/form-builder/formAssistant.types";

export type ChangeApplyStatus = "pending" | "applying" | "applied" | "skipped" | "failed";
export type ApprovalStatus = "pending" | "approved" | "cancelled";
export type NewFormStatus = "planning" | "pending" | "approved" | "cancelled" | "failed";
export type FormChangeStatus = "pending" | "opened" | "cancelled";

export type ChangeWithStatus = ProposedAssignmentChange & { status: ChangeApplyStatus };

export type AssignmentsAssistantEntry =
  | { id: number; kind: "user" | "note"; text: string }
  | ({ id: number } & AssistantErrorEntry)
  | { id: number; kind: "assistant"; text: string; choices: string[] }
  | { id: number; kind: "usedRows"; rows: AssignmentsAssistantResponse["usedRows"] }
  | {
      id: number;
      kind: "changes";
      // The changes are a plan first; only approved ones show on the page to apply.
      approval: ApprovalStatus;
      changes: ChangeWithStatus[];
    }
  | {
      id: number;
      kind: "newForm";
      request: AssistantNewFormRequest;
      plan: AssistantPlan | null;
      status: NewFormStatus;
    }
  | {
      id: number;
      kind: "formChange";
      // Opens the published form so the form assistant proposes the change in the
      // preview; the person applies and publishes it, or not.
      request: AssistantFormChangeRequest;
      status: FormChangeStatus;
    };

type NewEntry = AssignmentsAssistantEntry extends infer Entry
  ? Entry extends AssignmentsAssistantEntry
    ? Omit<Entry, "id">
    : never
  : never;

export interface AssignmentsTurnContext {
  tenantId?: string;
  focus?: AssignmentsFocus;
}

export interface PageChange {
  entryId: number;
  change: ChangeWithStatus;
}

interface AssignmentsAssistantState {
  isOpen: boolean;
  isBusy: boolean;
  tenantId: string | undefined;
  /**
   * The company a question asked with none selected turned out to be about; the
   * screen picks it so the next questions and any changes are for it.
   */
  answeredFor: { id: string; name: string } | null;
  messages: AssignmentsAssistantEntry[];
  setOpen: (open: boolean) => void;
  send: (text: string, context: AssignmentsTurnContext) => Promise<void>;
  approveChanges: (entryId: number) => void;
  cancelChanges: (entryId: number) => void;
  setChangeStatus: (entryId: number, key: string, status: ChangeApplyStatus) => void;
  setNewFormStatus: (entryId: number, status: NewFormStatus) => void;
  setFormChangeStatus: (entryId: number, status: FormChangeStatus) => void;
  addNote: (text: string) => void;
  addError: (text: string) => void;
  askToApply: (question: {
    tenantId: string;
    text: string;
    change: ProposedAssignmentChange;
  }) => void;
  stop: () => void;
  reset: () => void;
}

type AssignmentsRequestFn = (
  payload: AssignmentsAssistantRequest,
  signal?: AbortSignal,
) => Promise<AssignmentsAssistantResponse>;

type FormRequestFn = (
  payload: AssistantRequest,
  signal?: AbortSignal,
) => Promise<AssistantResponse>;

const MAX_HISTORY = 20;

function toHistory(messages: AssignmentsAssistantEntry[], next: string): AssistantChatMessage[] {
  const history: AssistantChatMessage[] = [];
  for (const entry of messages) {
    if (entry.kind === "user") history.push({ role: "user", content: entry.text });
    if (entry.kind === "assistant") history.push({ role: "assistant", content: entry.text });
  }
  history.push({ role: "user", content: next });
  const recent = history.slice(-MAX_HISTORY);
  while (recent.length > 1 && recent[0].role !== "user") recent.shift();
  return recent;
}

function isAbort(error: unknown): boolean {
  const name = (error as { name?: string })?.name;
  return name === "CanceledError" || name === "AbortError";
}

export function selectPageChanges(
  state: { messages: AssignmentsAssistantEntry[]; tenantId: string | undefined },
  tenantId: string | undefined,
): PageChange[] {
  const result: PageChange[] = [];
  if (!tenantId || state.tenantId !== tenantId) return result;
  for (const entry of [...state.messages].reverse()) {
    if (entry.kind !== "changes" || entry.approval !== "approved") continue;
    for (const change of entry.changes) {
      if (change.status !== "applied" && change.status !== "skipped") {
        result.push({ entryId: entry.id, change });
      }
    }
  }
  return result;
}

type SavedAssignmentsAssistant = Pick<
  AssignmentsAssistantState,
  "isOpen" | "isBusy" | "tenantId" | "answeredFor" | "messages"
>;

const INTERRUPTED_NOTE =
  "The page reloaded before the assistant answered. Nothing was changed. Ask again.";
const APPLY_INTERRUPTED_NOTE =
  "The page reloaded while a change was being applied, so it may already be saved. Check the page before applying it again.";

/**
 * A conversation saved mid-request comes back without it: a plan still being
 * written failed, a change being applied is offered again (Apply re-reads the
 * assignment first), and the person is told what happened.
 */
export function restoreAssignmentsAssistant(
  saved: SavedAssignmentsAssistant,
): SavedAssignmentsAssistant {
  let interrupted = saved.isBusy;
  let applyInterrupted = false;
  const messages = saved.messages.map((m): AssignmentsAssistantEntry => {
    if (m.kind === "newForm" && m.status === "planning") {
      interrupted = true;
      return { ...m, status: "failed" };
    }
    if (m.kind === "changes" && m.changes.some((c) => c.status === "applying")) {
      applyInterrupted = true;
      return {
        ...m,
        changes: m.changes.map((c) =>
          c.status === "applying" ? { ...c, status: "pending" as const } : c,
        ),
      };
    }
    return m;
  });
  const note = (text: string) =>
    messages.push({ id: Math.max(0, ...messages.map((m) => m.id)) + 1, kind: "note", text });
  if (interrupted) note(INTERRUPTED_NOTE);
  if (applyInterrupted) note(APPLY_INTERRUPTED_NOTE);
  return { ...saved, isBusy: false, messages };
}

export function createAssignmentsAssistantStore(
  request: AssignmentsRequestFn = requestAssignmentsAssistant,
  requestForm: FormRequestFn = requestFormAssistant,
  persistAs?: string,
) {
  let nextId = 1;
  let controller: AbortController | null = null;
  let generation = 0;

  const initializer: StateCreator<AssignmentsAssistantState> = (set, get) => {
    const push = (entry: NewEntry) =>
      set((state) => ({
        messages: [...state.messages, { ...entry, id: nextId++ } as AssignmentsAssistantEntry],
      }));

    const update = (
      entryId: number,
      patch: (entry: AssignmentsAssistantEntry) => AssignmentsAssistantEntry,
    ) =>
      set((state) => ({
        messages: state.messages.map((m) => (m.id === entryId ? patch(m) : m)),
      }));

    // A new question drops what the person did not act on: plans waiting for
    // approval, and approved changes nobody applied.
    const dropOpenProposals = () =>
      set((state) => ({
        messages: state.messages.map((m) => {
          if (m.kind === "changes") {
            return {
              ...m,
              approval: m.approval === "pending" ? "cancelled" : m.approval,
              changes: m.changes.map((c) =>
                c.status === "pending" || c.status === "failed"
                  ? { ...c, status: "skipped" as const }
                  : c,
              ),
            };
          }
          if (m.kind === "newForm" && (m.status === "pending" || m.status === "planning")) {
            return { ...m, status: "cancelled" as const };
          }
          if (m.kind === "formChange" && m.status === "pending") {
            return { ...m, status: "cancelled" as const };
          }
          return m;
        }),
      }));

    // The form builder plans the form, so the person approves the same plan
    // there and here, and it is built from it without asking again.
    async function planNewForm(
      newForm: AssistantNewFormRequest,
      context: AssignmentsTurnContext,
      signal: AbortSignal,
      turn: number,
    ) {
      push({ kind: "newForm", request: newForm, plan: null, status: "planning" });
      const entryId = nextId - 1;
      try {
        const response = await requestForm(
          {
            mode: "plan",
            tenantId: context.tenantId,
            messages: [{ role: "user", content: `${newForm.name}. ${newForm.brief}` }],
          },
          signal,
        );
        if (turn !== generation) return;
        if (response.outcome === "ok" && response.plan) {
          const plan = response.plan;
          update(entryId, (m) =>
            m.kind === "newForm" ? { ...m, plan, status: "pending" as const } : m,
          );
          return;
        }
        update(entryId, (m) => (m.kind === "newForm" ? { ...m, status: "failed" as const } : m));
        push({ kind: "assistant", text: response.reply, choices: [] });
      } catch (error) {
        if (turn !== generation) return;
        update(entryId, (m) => (m.kind === "newForm" ? { ...m, status: "failed" as const } : m));
        push(
          isAbort(error)
            ? { kind: "note", text: "Stopped. Nothing was changed." }
            : assistantErrorEntry(error),
        );
      }
    }

    return {
      isOpen: false,
      isBusy: false,
      tenantId: undefined,
      answeredFor: null,
      messages: [],
      setOpen: (open) => set({ isOpen: open }),

      send: async (text, context) => {
        const history = toHistory(get().messages, text);
        dropOpenProposals();
        push({ kind: "user", text });
        set({ tenantId: context.tenantId });
        const turn = ++generation;
        controller?.abort();
        controller = new AbortController();
        const ownController = controller;
        set({ isBusy: true });
        try {
          const response = await request(
            { tenantId: context.tenantId, focus: context.focus, messages: history },
            ownController.signal,
          );
          if (turn !== generation) return;
          if (response.company) {
            set({ tenantId: response.company.id, answeredFor: response.company });
            push({ kind: "note", text: `Reading ${response.company.name}.` });
          }
          push({ kind: "assistant", text: response.reply, choices: response.choices ?? [] });
          if (response.usedRows.length > 0) push({ kind: "usedRows", rows: response.usedRows });
          if (response.changes.length > 0) {
            push({
              kind: "changes",
              approval: "pending",
              changes: response.changes.map((c) => ({ ...c, status: "pending" as const })),
            });
          }
          if (response.newForm) {
            await planNewForm(response.newForm, context, ownController.signal, turn);
          }
          if (response.formChange) {
            push({ kind: "formChange", request: response.formChange, status: "pending" });
          }
        } catch (error) {
          if (turn !== generation) return;
          push(
            isAbort(error)
              ? { kind: "note", text: "Stopped. Nothing was changed." }
              : assistantErrorEntry(error),
          );
        } finally {
          if (controller === ownController) controller = null;
          if (turn === generation) set({ isBusy: false });
        }
      },

      approveChanges: (entryId) =>
        update(entryId, (m) =>
          m.kind === "changes" && m.approval === "pending" ? { ...m, approval: "approved" } : m,
        ),

      cancelChanges: (entryId) => {
        update(entryId, (m) =>
          m.kind === "changes" && m.approval === "pending"
            ? {
                ...m,
                approval: "cancelled",
                changes: m.changes.map((c) => ({ ...c, status: "skipped" as const })),
              }
            : m,
        );
        push({ kind: "note", text: "Plan cancelled. Nothing was changed." });
      },

      setChangeStatus: (entryId, key, status) =>
        update(entryId, (m) =>
          m.kind === "changes"
            ? { ...m, changes: m.changes.map((c) => (c.key === key ? { ...c, status } : c)) }
            : m,
        ),

      setNewFormStatus: (entryId, status) =>
        update(entryId, (m) => (m.kind === "newForm" ? { ...m, status } : m)),

      setFormChangeStatus: (entryId, status) =>
        update(entryId, (m) => (m.kind === "formChange" ? { ...m, status } : m)),

      addNote: (text) => push({ kind: "note", text }),

      addError: (text) => push({ kind: "error", text }),

      // The person already approved this form for this slot; what is left is the
      // yes or no on the assignment itself, on the page and in the panel.
      askToApply: ({ tenantId, text, change }) => {
        push({ kind: "assistant", text, choices: [] });
        push({
          kind: "changes",
          approval: "approved",
          changes: [{ ...change, status: "pending" }],
        });
        set({ tenantId, isOpen: true });
      },

      stop: () => controller?.abort(),

      reset: () => {
        generation += 1;
        controller?.abort();
        set({ isBusy: false, messages: [], tenantId: undefined, answeredFor: null });
      },
    };
  };

  if (!persistAs) return create<AssignmentsAssistantState>()(initializer);
  // Per browser tab: a reload keeps the conversation and its proposals on the
  // page, another tab starts clean.
  return create<AssignmentsAssistantState>()(
    persist(initializer, {
      name: persistAs,
      storage: createJSONStorage(() => tabStorage),
      partialize: ({
        isOpen,
        isBusy,
        tenantId,
        answeredFor,
        messages,
      }): SavedAssignmentsAssistant => ({ isOpen, isBusy, tenantId, answeredFor, messages }),
      merge: (saved, current) => {
        if (!saved) return current;
        const restored = restoreAssignmentsAssistant(saved as SavedAssignmentsAssistant);
        nextId = Math.max(0, ...restored.messages.map((m) => m.id)) + 1;
        return { ...current, ...restored };
      },
    }),
  );
}

export const useAssignmentsAssistantStore = createAssignmentsAssistantStore(
  requestAssignmentsAssistant,
  requestFormAssistant,
  "form-builder-assignments-assistant",
);
